import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";
import { RINKS, RINK_LABEL, RINK_BADGE, ROLE_LABEL, ROLE_BADGE } from "@/lib/rinks";

const EMPTY = { name: "", email: "", password: "", role: "staff", rink: "sligo" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode:'new'} | {mode:'edit', user}
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [params] = useSearchParams();
  const rinkFilter = params.get("rink");
  const visibleUsers = rinkFilter ? users.filter((u) => u.rink === rinkFilter || !u.rink) : users;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, rink: rinkFilter || EMPTY.rink }); setError(""); setDialog({ mode: "new" }); };
  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: "", role: u.role, rink: u.rink || "sligo" });
    setError("");
    setDialog({ mode: "edit", user: u });
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (dialog.mode === "new") {
        await api.post("/users", { ...form, rink: form.role === "staff" || form.role === "medic" ? form.rink : null });
        toast.success("User created");
      } else {
        const payload = { name: form.name, role: form.role, rink: form.role === "staff" || form.role === "medic" ? form.rink : null };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${dialog.user.id}`, payload);
        toast.success("User updated");
      }
      setDialog(null);
      load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Access control</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase text-slate-900">{rinkFilter ? `${RINK_LABEL[rinkFilter]} users` : "Staff users"}</h1>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="new-user-button">
          <Plus className="w-4 h-4" /> New user
        </button>
      </div>

      <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a] overflow-x-auto" data-testid="users-table">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr className="bg-slate-900 text-white font-mono-tight uppercase text-xs tracking-widest">
              <th className="p-3.5 text-left font-bold">Name</th>
              <th className="p-3.5 text-left font-bold">Email</th>
              <th className="p-3.5 text-left font-bold">Role</th>
              <th className="p-3.5 text-left font-bold">Rink access</th>
              <th className="p-3.5 text-left font-bold"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && visibleUsers.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500">No users</td></tr>
            )}
            {visibleUsers.map((u) => (
              <tr key={u.id} className="border-t-2 border-slate-900 hover:bg-sky-50/70 transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="p-3.5 text-sm font-bold">{u.name || "—"}</td>
                <td className="p-3.5 text-sm font-mono-tight">{u.email}</td>
                <td className="p-3.5"><span className={`badge-brutal ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span></td>
                <td className="p-3.5">
                  {(u.role === "staff" || u.role === "medic") && u.rink
                    ? <span className={`badge-brutal ${RINK_BADGE[u.rink]}`}>{RINK_LABEL[u.rink]}</span>
                    : <span className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">Both rinks</span>}
                </td>
                <td className="p-3.5 whitespace-nowrap">
                  <div className="flex gap-2">
                    <button className="btn-secondary !px-2.5 !py-1.5" onClick={() => openEdit(u)} title="Edit" data-testid={`user-edit-${u.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="btn-danger !px-2.5 !py-1.5" onClick={() => remove(u)} title="Delete" data-testid={`user-delete-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="border-4 border-slate-900 shadow-[8px_8px_0px_0px_#0f172a] rounded-none max-w-lg bg-white" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase text-slate-900">
              {dialog?.mode === "new" ? "New user" : "Edit user"}
            </DialogTitle>
          </DialogHeader>
          {error && (
            <div className="border-2 border-rose-500 bg-rose-50 text-rose-700 p-3 font-mono-tight text-xs font-bold" data-testid="user-form-error">{error}</div>
          )}
          <form onSubmit={save} className="grid grid-cols-1 gap-4 mt-2" data-testid="user-form">
            <div>
              <label className="label-brutal" htmlFor="usr-name">Name *</label>
              <input id="usr-name" className="input-brutal" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div>
              <label className="label-brutal" htmlFor="usr-email">Email *</label>
              <input id="usr-email" type="email" className="input-brutal" required disabled={dialog?.mode === "edit"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div>
              <label className="label-brutal" htmlFor="usr-password">
                {dialog?.mode === "new" ? "Password * (min 8 chars)" : "New password (leave blank to keep)"}
              </label>
              <input id="usr-password" type="password" className="input-brutal" minLength={8} required={dialog?.mode === "new"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div>
              <label className="label-brutal" htmlFor="usr-role">Role *</label>
              <select id="usr-role" className="input-brutal" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                <option value="admin">Admin — full access, both rinks</option>
                <option value="staff">Staff — one rink only</option>
                <option value="viewer">Viewer — read-only, both rinks</option>
                <option value="medic">Medic — medical incidents only, one rink</option>
              </select>
            </div>
            {(form.role === "staff" || form.role === "medic") && (
              <div>
                <label className="label-brutal" htmlFor="usr-rink">Assigned rink *</label>
                <select id="usr-rink" className="input-brutal" value={form.rink} onChange={(e) => setForm({ ...form, rink: e.target.value })} data-testid="user-rink-select">
                  {RINKS.map((r) => <option key={r} value={r}>{RINK_LABEL[r]}</option>)}
                </select>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-2">
              <button type="button" className="btn-secondary" onClick={() => setDialog(null)} data-testid="user-cancel-button">Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="user-submit-button">
                {busy ? "Saving…" : "Save user"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
